import { useState, useEffect, useRef, useTransition } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DragMoveEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import _ from 'lodash';

import './trashIconStyle.css';
import { SortableItem } from './SortableItem';
import { RiDragMove2Line } from 'react-icons/ri';

type Index = {
  normal: number | undefined;
  throttled: number | undefined;
};

export type SortableItem = {
  id: string;
  label: string | JSX.Element;
  isDisabled?: boolean;
  actionsNode?: JSX.Element | null;
};

interface SortableProps {
  data: SortableItem[][];
  containerClassName?: string;
  containerHeaders?: {
    firstContainer?: string | JSX.Element;
    secondContainer?: string | JSX.Element;
  };
  hasExtraDeleteContainer?: boolean;
  onItemDeleteFromFirstContainer?: (
    itemId: string | undefined
  ) => Promise<'fulfilled' | 'rejected' | void>;
  onItemAddToFirstContainer?: (
    itemId: string | undefined,
    order?: number
  ) => Promise<'fulfilled' | 'rejected' | void>;
  onItemAddToSecondContainer?: (
    itemId: string | undefined,
    order?: number
  ) => Promise<'fulfilled' | 'rejected' | void>;
  onFirstContainerReorder?: (
    ids: (string | undefined)[]
  ) => Promise<'fulfilled' | 'rejected' | void>;
  onSecondContainerReorder?: (
    ids: (string | undefined)[]
  ) => Promise<'fulfilled' | 'rejected' | void>;
}

export function Sortable({
  data,
  containerClassName,
  containerHeaders,
  hasExtraDeleteContainer,
  onItemDeleteFromFirstContainer,
  onItemAddToFirstContainer,
  onItemAddToSecondContainer,
  onFirstContainerReorder,
  onSecondContainerReorder,
}: SortableProps) {
  const firstContainerRef = useRef<HTMLDivElement>(null);
  const secondContainerRef = useRef<HTMLDivElement>(null);
  const extraContainerRef = useRef<HTMLDivElement>(null);
  const trashIconRef = useRef<HTMLSpanElement>(null);
  const trashIconCoverRef = useRef<HTMLSpanElement>(null);

  const trashTimeout = useRef<NodeJS.Timeout>();

  const [firstContainerItems, setFirstContainerItems] = useState<
    SortableItem[]
  >([
    ...data[0].map((d) => {
      return { ...d, id: d.id + ':' + 'first-container' };
    }),
    {
      id: 'extra-space-1:first-container',
      label: <div className="hidden"></div>,
    },
  ]);
  const [secondContainerItems, setSecondContainerItems] = useState<
    SortableItem[]
  >([
    ...data[1].map((d) => {
      return { ...d, id: d.id + ':' + 'second-container' };
    }),
    {
      id: 'extra-space-2:second-container',
      label: <div className="hidden"></div>,
    },
  ]);
  const [extraContainerItems, setExtraContainerItems] = useState<
    SortableItem[]
  >([
    {
      id: 'extra-container-trash',
      label: <div className="w-full h-[1px]"></div>,
    },
  ]);
  const [activeItem, setActiveItem] = useState<SortableItem | null | undefined>(
    null
  );
  const [isItemDeleted, setIsItemDeleted] = useState(false);
  const [resetContainers, setResetContainers] = useState(false);
  const [firstContainerIdsOnStart, setFirstContainerIdsOnStart] = useState<
    (string | undefined)[]
  >([]);
  const [secondContainerIdsOnStart, setSecondContainerIdsOnStart] = useState<
    (string | undefined)[]
  >([]);

  const [isPending, startTransition] = useTransition();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setFirstContainerItems([
      ...data[0].map((d) => {
        return { ...d, id: d.id + ':' + 'first-container' };
      }),
      {
        id: 'extra-space-1:first-container',
        label: <div className="hidden"></div>,
      },
    ]);

    setSecondContainerItems([
      ...data[1].map((d) => {
        return { ...d, id: d.id + ':' + 'second-container' };
      }),
      {
        id: 'extra-space-2:second-container',
        label: <div className="hidden"></div>,
      },
    ]);
  }, [data]);

  useEffect(() => {
    if (isItemDeleted) {
      secondContainerRef.current?.classList.remove('hidden');
      extraContainerRef.current?.classList.remove('block');
      extraContainerRef.current?.classList.add('hidden');

      setIsItemDeleted(false);
    }
  }, [isItemDeleted]);

  useEffect(() => {
    if (resetContainers) {
      setFirstContainerItems([
        ...data[0].map((d) => {
          return { ...d, id: d.id + ':' + 'first-container' };
        }),
        {
          id: 'extra-space-1:first-container',
          label: <div className="hidden"></div>,
        },
      ]);
      setSecondContainerItems([
        ...data[1].map((d) => {
          return { ...d, id: d.id + ':' + 'second-container' };
        }),
        {
          id: 'extra-space-2:second-container',
          label: <div className="hidden"></div>,
        },
      ]);
      setExtraContainerItems([
        {
          id: 'extra-container-trash',
          label: <div className="w-full h-[1px]"></div>,
        },
      ]);

      setResetContainers(false);
    }
  }, [resetContainers]);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragMove={handleDragMove}
      onDragEnd={handleDragEnd}
    >
      <div className="w-full flex justify-center items-start space-x-4">
        <div
          ref={firstContainerRef}
          className={`order-3 w-full ${containerClassName}`}
        >
          {containerHeaders?.firstContainer}
          <SortableContext
            items={firstContainerItems}
            strategy={verticalListSortingStrategy}
          >
            {firstContainerItems.map((item) => {
              if (!item.isDisabled) {
                return (
                  <div className="mt-3">
                    <SortableItem
                      key={item.id}
                      id={item.id}
                      actionsNode={item.actionsNode}
                    >
                      <div
                        className={`h-full ${
                          activeItem?.id === item.id && 'opacity-60'
                        }`}
                      >
                        {item.label}
                      </div>
                    </SortableItem>
                  </div>
                );
              } else {
                return (
                  <div
                    key={item.id}
                    className={`mt-3 h-[100px] rounded-md bg-white shadow ${
                      activeItem?.id === item.id && 'opacity-60'
                    }`}
                  >
                    {item.label}
                  </div>
                );
              }
            })}
          </SortableContext>
        </div>
        <div
          ref={secondContainerRef}
          className={`order-2 w-full ${containerClassName}`}
        >
          {containerHeaders?.secondContainer}
          <SortableContext
            items={secondContainerItems}
            strategy={verticalListSortingStrategy}
          >
            {secondContainerItems.map((item) => {
              if (!item.isDisabled) {
                return (
                  <div className="mt-3">
                    <SortableItem
                      key={item.id}
                      id={item.id}
                      actionsNode={item.actionsNode}
                    >
                      <div
                        className={`h-full ${
                          activeItem?.id === item.id && 'opacity-60'
                        }`}
                      >
                        {item.label}
                      </div>
                    </SortableItem>
                  </div>
                );
              } else {
                return (
                  <div
                    key={item.id}
                    className={`relative mt-3 h-[100px] rounded-md bg-white shadow opacity-60`}
                  >
                    {item.label}
                    <div className="absolute top-1/2 -translate-y-1/2 right-4">
                      {item.actionsNode}
                    </div>
                  </div>
                );
              }
            })}
          </SortableContext>
        </div>
        <div
          ref={extraContainerRef}
          className={`hidden order-1 w-full ${containerClassName}`}
        >
          <div className="w-full h-1/2 flex justify-center items-end translate-y-[60px]">
            <span ref={trashIconRef} className="trash">
              <span ref={trashIconCoverRef}></span>
              <i></i>
            </span>
          </div>
          <SortableContext
            items={extraContainerItems}
            strategy={verticalListSortingStrategy}
          >
            {extraContainerItems.map((e) => {
              return (
                <SortableItem key={e.id} id={e.id} isHidden={true}>
                  <div>{e.label}</div>
                </SortableItem>
              );
            })}
          </SortableContext>
        </div>
        <DragOverlay>
          {activeItem ? (
            <div className="w-full scale-y-[1.05] scale-x-[1.03]">
              <div
                className={`relative w-full h-[100px] rounded-md bg-white shadow-md`}
              >
                <div
                  className={`absolute top-1/2 -translate-y-1/2 left-4 p-[2px] rounded text-[24px] text-warmGray-300 ${
                    !activeItem.isDisabled &&
                    'cursor-pointer hover:bg-gray-300 hover:text-white'
                  } transition-transform`}
                >
                  {!activeItem.isDisabled && <RiDragMove2Line />}
                </div>
                <div className="w-full h-full">{activeItem.label}</div>
                <div className="absolute top-1/2 -translate-y-1/2 right-4">
                  {activeItem.actionsNode}
                </div>
              </div>
            </div>
          ) : null}
        </DragOverlay>
      </div>
    </DndContext>
  );

  function handleDragStart(event: DragStartEvent) {
    if (trashTimeout.current) {
      clearTimeout(trashTimeout.current);
    }

    const { active } = event;

    const activeSplits = (active.id as string).split(':');
    const activeContainer = activeSplits[1];

    const item = firstContainerItems
      .concat(secondContainerItems)
      .concat(extraContainerItems)
      .find((i) => i.id === active.id);

    setActiveItem(item);

    setFirstContainerIdsOnStart(firstContainerItems.map((f) => f.id));
    setSecondContainerIdsOnStart(secondContainerItems.map((s) => s.id));

    if (hasExtraDeleteContainer && activeContainer === 'first-container') {
      secondContainerRef.current?.classList.add('hidden');
      extraContainerRef.current?.classList.remove('hidden');
      extraContainerRef.current?.classList.add('block');

      if (trashIconCoverRef.current) {
        trashIconCoverRef.current.style.animation = '0.5s cover-open forwards';
      }
    }
  }

  function handleDragMove(event: DragMoveEvent) {
    const { active, over } = event;

    const activeSplits = (active.id as string).split(':');
    const activeContainer = activeSplits[1];

    const overSplits = (over?.id as string | undefined)?.split(':');
    const overId = overSplits?.[0];
    const overContainer = overSplits?.[1];

    if (isPending) return;

    const activeIndexFirstContainer = firstContainerItems.findIndex(
      (i) => i.id === active.id
    );
    const overIndexFirstContainer = firstContainerItems.findIndex(
      (i) => i.id === over?.id
    );
    const activeIndexSecondContainer = secondContainerItems.findIndex(
      (i) => i.id === active.id
    );
    const overIndexSecondContainer = secondContainerItems.findIndex(
      (i) => i.id === over?.id
    );

    if (activeContainer !== overContainer) {
      if (
        overContainer === 'first-container' &&
        overId !== 'extra-container-trash'
      ) {
        if (activeIndexFirstContainer === -1) {
          const activeItem = secondContainerItems
            .concat(extraContainerItems)
            .find((i) => i.id === active.id);

          if (activeItem)
            startTransition(() => {
              setFirstContainerItems((items) => {
                const activeIndexFirstContainer = items.findIndex(
                  (i) => i.id === active.id
                );
                const overIndexFirstContainer = items.findIndex(
                  (i) => i.id === over?.id
                );

                if (activeIndexFirstContainer == -1) {
                  items.splice(overIndexFirstContainer, 0, activeItem);
                }

                return items;
              });

              setSecondContainerItems((items) => {
                return items.filter((f) => f.id !== active.id);
              });
            });
        } else {
          startTransition(() => {
            setFirstContainerItems((items) => {
              return arrayMove(
                items,
                activeIndexFirstContainer,
                overIndexFirstContainer
              );
            });
          });
        }
      } else if (
        activeContainer === 'first-container' &&
        overId === 'extra-container-trash'
      ) {
        if (
          activeIndexFirstContainer !== -1 ||
          activeIndexSecondContainer !== -1
        ) {
          const activeItem = firstContainerItems
            .concat(secondContainerItems)
            .find((i) => i.id === active.id);

          if (activeItem) {
            startTransition(() => {
              setExtraContainerItems((items) => {
                const activeIndexExtraContainer = items.findIndex(
                  (i) => i.id === active.id
                );
                const overIndexExtraContainer = items.findIndex(
                  (i) => i.id === over?.id
                );

                if (activeIndexExtraContainer === -1) {
                  items.splice(overIndexExtraContainer, 0, activeItem);
                }

                return items;
              });

              setFirstContainerItems((items) => {
                return items.filter((f) => f.id !== active.id);
              });

              setSecondContainerItems((items) => {
                return items.filter((f) => f.id !== active.id);
              });
            });
          }
        }
      }
    } else if (
      overContainer === 'second-container' &&
      activeContainer === 'second-container'
    ) {
      if (
        activeIndexSecondContainer === -1 &&
        overIndexSecondContainer !== -1
      ) {
        const activeItem = firstContainerItems
          .concat(extraContainerItems)
          .find((i) => i.id === active.id);

        if (activeItem)
          startTransition(() => {
            setSecondContainerItems((items) => {
              const activeIndexSecondContainer = items.findIndex(
                (i) => i.id === active.id
              );
              const overIndexSecondContainer = items.findIndex(
                (i) => i.id === over?.id
              );

              if (activeIndexSecondContainer === -1) {
                items.splice(overIndexSecondContainer, 0, activeItem);
              }

              return items;
            });

            setFirstContainerItems((items) => {
              return items.filter((f) => f.id !== active.id);
            });
          });
      }
    } else if (
      activeContainer === 'first-container' &&
      overContainer === 'first-container'
    ) {
      if (activeIndexFirstContainer !== -1 && overIndexFirstContainer !== -1) {
        const activeItem = firstContainerItems.find((i) => i.id === active.id);

        if (activeItem) {
          startTransition(() => {
            setFirstContainerItems((items) => {
              const activeIndexFirstContainer = items.findIndex(
                (i) => i.id === active.id
              );
              const overIndexFirstContainer = items.findIndex(
                (i) => i.id === over?.id
              );

              return arrayMove(
                items,
                activeIndexFirstContainer,
                overIndexFirstContainer
              );
            });
          });
        }
      } else if (
        activeIndexFirstContainer === -1 &&
        overIndexFirstContainer !== -1
      ) {
        const activeItem = secondContainerItems
          .concat(extraContainerItems)
          .find((i) => i.id === active.id);

        if (activeItem)
          startTransition(() => {
            setFirstContainerItems((items) => {
              const activeIndexFirstContainer = items.findIndex(
                (i) => i.id === active.id
              );
              const overIndexFirstContainer = items.findIndex(
                (i) => i.id === over?.id
              );

              if (activeIndexFirstContainer == -1) {
                items.splice(overIndexFirstContainer, 0, activeItem);
              }

              return items;
            });

            setExtraContainerItems((items) => {
              return items.filter((f) => f.id !== active.id);
            });
          });
      }
    }
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    let activeId = active.id as string;
    let overId = over?.id as string;

    activeId = activeId?.split(':')[0];
    activeId = activeId?.split(':')[0];
    overId = overId?.split(':')[0];
    overId = overId?.split(':')[0];

    if (
      firstContainerIdsOnStart?.length &&
      firstContainerItems.length < firstContainerIdsOnStart?.length &&
      onItemDeleteFromFirstContainer
    ) {
      setFirstContainerItems((items) => {
        return items.filter((f) => f.id != active.id);
      });

      const status = await onItemDeleteFromFirstContainer(activeId);

      if (status !== 'fulfilled') {
        setResetContainers(true);
      }

      if (trashIconCoverRef.current) {
        trashIconCoverRef.current.style.animation = '1.5s cover-bounce';
      }
    }

    trashTimeout.current = setTimeout(() => {
      setIsItemDeleted(true);
    }, 1000);

    if (
      firstContainerIdsOnStart?.length &&
      firstContainerItems.length > firstContainerIdsOnStart?.length &&
      onItemAddToFirstContainer &&
      firstContainerItems.find((i) => i.id === overId + ':second-container')
    ) {
      setFirstContainerIdsOnStart(firstContainerItems.map((i) => i.id));

      const statusAdd = await onItemAddToFirstContainer(
        activeId,
        firstContainerItems.findIndex(
          (i) => i.id === overId + ':second-container'
        )
      );

      if (statusAdd !== 'fulfilled') {
        setResetContainers(true);
      }
    }

    if (
      secondContainerIdsOnStart?.length &&
      secondContainerItems.length > secondContainerIdsOnStart?.length &&
      onItemAddToSecondContainer &&
      secondContainerItems.find((i) => i.id === overId + '-first-container')
    ) {
      const statusAdd = await onItemAddToSecondContainer(
        activeId,
        secondContainerItems.findIndex(
          (i) => i.id === overId + '-first-container'
        )
      );

      if (statusAdd !== 'fulfilled') {
        setResetContainers(true);
      }
    }

    if (
      firstContainerIdsOnStart?.length &&
      firstContainerItems.length === firstContainerIdsOnStart?.length &&
      !areArraysEqual(
        firstContainerIdsOnStart,
        firstContainerItems.map((f) => f.id)
      ) &&
      onFirstContainerReorder
    ) {
      const status = await onFirstContainerReorder(
        firstContainerItems
          .map((i) => i.id.split(':')[0])
          .filter((id) => id !== 'extra-space-1')
      );

      if (status !== 'fulfilled') {
        setResetContainers(true);
      }
    }

    if (
      secondContainerIdsOnStart?.length &&
      secondContainerItems.length === secondContainerIdsOnStart?.length &&
      !areArraysEqual(
        secondContainerIdsOnStart,
        secondContainerItems.map((f) => f.id)
      ) &&
      onSecondContainerReorder
    ) {
      const status = await onSecondContainerReorder(
        secondContainerItems
          .map((i) => i.id.split(':')[0])
          .filter((id) => id !== 'extra-space-2')
      );

      if (status !== 'fulfilled') {
        setResetContainers(true);
      }
    }

    setActiveItem(null);
  }
}

function areArraysEqual(
  firstArray: (string | undefined)[],
  secondArray: (string | undefined)[]
) {
  const firstArrayLength = firstArray.length;
  const secondArrayLength = secondArray.length;

  if (firstArrayLength !== secondArrayLength) return false;

  for (let i = 0; i < firstArrayLength; i++) {
    if (firstArray[i] !== secondArray[i]) {
      return false;
    }
  }

  return true;
}
